import * as React from "react";
import { Button, Col, Row } from "react-bootstrap";
import styled from "styled-components";

import { ReportTitle, ReportTitleText } from "../../../../parts/report-title";
import { Job } from "../../../../../../../../../server/models/job";
import { CpCorrectionValueSettingTable } from "../../correction-value/cp-correction-value-setting-table";
import { ChangeRateValue } from "../../../../../../../../../server/models/cp2";
import { UserTable } from "../../result/user-table";
import { Constant } from "../../../../../../../../../server/analytics/v3/output/executive/demand-forecast/aggregate";
import {
	CurrentProductCorrectionValue,
	CurrentProductInitialValue,
	SimulationPredictionData,
	SimulationReferenceSourceData,
} from "../../../../../../../../../server/types/request/report/simulation";
import { TestOnlyConceptKey } from "../../../../../../../../../server/models/concept";
import { CPSalesVolumeDataTable } from "./sales-volume";
import { makeSimulator } from "../../../../../../../../../shared/simulation";

const SubLabelArea = styled.div`
	font-weight: bold;
`;

const TableArea = styled.div`
	table {
		margin: 13px 0;
	}
`;

type Props = {
	constValue: Constant;
	cp: CurrentProductCorrectionValue;
	data: { [key in TestOnlyConceptKey]?: SimulationPredictionData };
	initialValue: CurrentProductInitialValue;
	referenceSourceData: SimulationReferenceSourceData;
	hasEditRole: boolean;
	job: Job;
	maxRecognition: number;
	goalRecognitionRate: number;
	onChangeRatio: <K extends keyof ChangeRateValue>(name: K, value: ChangeRateValue[K]) => void;
	onSubmit: () => void;
};

export const CPParameterSetting = React.memo(
	({
		constValue,
		cp,
		data,
		initialValue,
		referenceSourceData,
		goalRecognitionRate,
		hasEditRole,
		job,
		maxRecognition,
		onChangeRatio,
		onSubmit,
	}: Props) => {
		const { purchaseIntent: segmentPurchaseIntent } = initialValue;
		const results = React.useMemo(() => {
			if (cp?.correctionFactor == null) return {};
			const simulator = makeSimulator(referenceSourceData, initialValue, constValue, goalRecognitionRate);
			return {
				test1: simulator.calc(data.test1.initialValue.purchaseIntent, {}, cp),
				test2: data.test2 != null ? simulator.calc(data.test2.initialValue.purchaseIntent, {}, cp) : undefined,
			};
		}, [constValue, cp, data.test1, data.test2, initialValue, goalRecognitionRate, referenceSourceData]);
		return (
			<>
				<Row>
					<Col md={12}>
						<ReportTitle
							style={{
								marginBottom: "1rem",
							}}
						>
							現行品/類似品の商品認知率修正
						</ReportTitle>
					</Col>
				</Row>

				<Row>
					{/* 購入率率修正との位置調整のため常に表示させておく */}
					<Col md={{ span: 1 }}>
						{/* spacer */}
						<></>
					</Col>
					<Col>
						<Row
							style={{
								marginBottom: "30px",
							}}
						>
							<Col md={12}>
								<ReportTitleText
									className="border"
									style={{
										color: "#C00000",
										padding: "10px",
									}}
								>
									商品認知率のみの変更の場合でも、上段の設定が必要です
									<br />
									➀上段の＜購入率 修正＞で「初期値から変更しない」を選択
									<br />
									➁＜購入率 更新＞のボタンをクリック
									<br />
									③下段の＜商品認知率 修正＞で入力完了した後に＜推定値 算出＞ボタンをクリック
								</ReportTitleText>
							</Col>
						</Row>
						<Row style={{ marginBottom: "3rem" }}>
							<Col md={12}>
								<TableArea>
									<CpCorrectionValueSettingTable
										correctionValue={cp}
										hasEditRole={hasEditRole}
										initRecognition={job.currentProductRecognitionRate ?? 0}
										maxRecognition={maxRecognition}
										onChange={onChangeRatio}
									/>
								</TableArea>
							</Col>

							{hasEditRole && (
								<Col md={{ offset: 2, span: 2 }}>
									{/*block*/}
									<Button onClick={onSubmit} variant="secondary">
										推定値 算出
									</Button>
								</Col>
							)}
						</Row>
						{!!results.test1 && (
							<>
								<Row style={{ marginBottom: "2rem" }}>
									<Col md={12}>
										{/* category user table */}
										<UserTable
											constValue={constValue}
											segmentPurchaseIntent={segmentPurchaseIntent}
											user={referenceSourceData.user}
										/>
									</Col>
								</Row>

								{/* current product sales volume */}
								<Row>
									<Col md={3}>
										<SubLabelArea>テスト品1</SubLabelArea>

										<CPSalesVolumeDataTable data={results.test1} />
									</Col>

									{results.test2 && (
										<Col md={3}>
											<SubLabelArea>テスト品2</SubLabelArea>

											<CPSalesVolumeDataTable data={results.test2} />
										</Col>
									)}
								</Row>
							</>
						)}
					</Col>
				</Row>
			</>
		);
	},
);
